// @ts-check

import React, { useState } from "react";
import isArray from "lodash/isArray";
import { Button, Collapse } from "antd";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";

import { ConfigureRelationshipMatch } from "./configure-relationship-match";
import { colors } from "@evolved/constants";
import { findField } from "../find-field";

/**
 * @typedef {Object} Props
 *
 * @prop {import("../domain").ImportField[]} importFields
 * @prop {import("../import-state/import-state").ImportStateService} service
 * @prop {import("../domain").ImportState} state
 *
 * @prop {() => void} onBack
 * @prop {(() => void) | null} onNext
 */

/**
 * @param {Props} props
 */
export const ConfigureRelationshipMatches = (props) => {
  const {
    importFields,
    service,
    state,
    onNext,
    onBack,
  } = props;

  const {
    relationshipMatches,
  } = state;

  const dataIndexes = Object.keys(relationshipMatches).sort();

  const defaultIndex = dataIndexes.find(dataIndex => {
    return relationshipMatches[dataIndex].config.dataIndexes.length === 0;
  }) ?? dataIndexes[0];

  const [currentDataIndex, setCurrentDataIndex] = useState(
    defaultIndex
  );

  /** @type{import("antd").CollapseProps["items"]} */
  const options = dataIndexes.map((dataIndex) => {
    const { config, dataIndexOptions } = relationshipMatches[dataIndex];

    const hasSelections = !!config.dataIndexes.flat().length;

    /** @type {"success" | "warning"} */
    let level = "success";

    if (!hasSelections || state.reducedRows.some(row => {
      return row.info.some((info) => {
        return info.type === "relationship_match_info" && info.level === "warning";
      });
    })) {
      level = "warning";
    }

    const icon = level === "success" ?
      <CheckCircleTwoTone style={{ marginLeft: "4px" }} twoToneColor={colors.SUCCESS} /> :
      <CloseCircleTwoTone style={{ marginLeft: "4px" }} twoToneColor={colors.URGENT} />
      ;

    const relationshipField = findField(dataIndex)(importFields);
    if (relationshipField.type !== "SET") {
      throw new Error("relationship field should be of type SET");
    }

    return {
      key: dataIndex,
      label: <div>{findField(dataIndex)(importFields).name}{icon}</div>,
      children: (
        <ConfigureRelationshipMatch
          {...{
            dataIndexOptions,
            reducedRows: state.reducedRows,
            relationshipField: findField(dataIndex)(importFields),
            relationshipMatchConfig: config,
            service,
          }}
        />
      )
    };
  });

  return (
    <>
      <div className="mb-1">
        Which field(s) on the related entity match CRM records to your import rows?
      </div>
      <div className="mb-2">
        <strong>Unmatched relationships will be ignored.</strong>
      </div>
      <div className="mb-4">
        <Collapse
          accordion
          activeKey={currentDataIndex}
          items={options}
          onChange={(dataIndex) => {
            setCurrentDataIndex(isArray(dataIndex) ? dataIndex[0] : dataIndex);
          }}
          size="small"
        />
      </div>
      <div className="flex justify-end">
        <Button className="mr-2" onClick={onBack}>Back</Button>
        <Button type="primary" disabled={!onNext} onClick={onNext ?? undefined}
        >Next</Button>
      </div>
    </>
  );
};
